import React from 'react';
import {useServiceBase} from "../../Components/ReactTable/useServiceBase";
import EditableReactTableV2 from "../../Components/ReactTable/EditableReactTableV2";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import _ from "lodash";
import {
    EditFormBooleanDropdownInputField,
    EditFormDropdownInputField,
    EditFormLabelErrorWrapper,
    EditFormMultiDropdownInputField,
    EditFormNumberInputField,
    EditFormTextInputField
} from "../../Components/ReactTable/EditRecordForm";
import IconDropdown from "../../Components/IconDropdown";
import MarketServiceNavLinks from "../../Components/MarketServiceNavLinks";
import {findDuplicates} from "../../../utils/util";
import PropTypes from "prop-types";
import {getCellForService, getFilterValueForService} from "../../Components/ReactTable/common_dropdown_options";

export function EwbEditField({id, form}) {
    const retailerCorporateTotals = form.watch("retailer_corporate_totals")
    form.setValue(id, retailerCorporateTotals ? 'Yes' : '')
    return (
        <EditFormLabelErrorWrapper id={id} form={form} label={'EWB'}>
            <input
                type={"text"}
                id={id}
                disabled={true}
                readOnly={true}
                step={"any"}
                {...(form.register(id))}/>
        </EditFormLabelErrorWrapper>
    );
}

const MarketServiceContent = ({
                                   period,
                                   outletsDropdown,
                                   accountDropdown,
                                   legacyAccountTypeDropdown,
                                   geographyLevelDropdown,
                                   accountTypeDropdown,
                                   geographyTypeDropdown,
                                   geographyCompositionDropdown,
                                   releasabilityDropdown,
                                   weeklyReleasabilityDropdown,
                                   measureAvailabilityDropdown,
                                   geoStoreLevelAllowabilityDropdown,
                                   decodeTablesLoading,
                                   outletChannelSourceMap
                               }) => {
    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/markets?period=${period}`;
    const {loading, data, onRowEditConfirmed, onRowDeleteConfirmed} = useServiceBase({
        endpointUrl,
        urlContainsParams: true
    });


    function IndexEditField({id, form}) {
        const formRegisterOptions = {required: true, valueAsNumber: true};
        const indexValue = form.getValues('index');
        if (indexValue === null) {
            const maxIndex = _.max(data.data.map(x => x.index));
            form.setValue(id, maxIndex + 1);
        }

        return (
            <EditFormLabelErrorWrapper id={id} form={form} label={"Market Id"} isRequired>
                <input
                    type={"text"}
                    id={id}
                    disabled={true}
                    readOnly={true}
                    step={"any"}
                    {...(form.register(id, formRegisterOptions))}/>
            </EditFormLabelErrorWrapper>
        );
    }


    function getArrayCellForService(value, dropdownValues) {

        const displayValue = convertToArray(value).map(item =>
        {return _.find(dropdownValues, {value: item})?.label|| item })
        return <div>{displayValue.join(', ')} </div>
    }

    function convertToArray(value) {
        if (!Array.isArray(value)) {
            return [value];
        }
        return value;
    }


    function getFilterArrayValueForService(row, columnIds, filterValue, dropdownValues) {
        const recordValue = row.original[columnIds];
        const label = convertToArray(recordValue).map(item =>
        {return _.find(dropdownValues, {value: item})?.label.toLowerCase()|| item })
        return !filterValue || Boolean(`${recordValue} ${label}`.includes(filterValue.toLowerCase()));
    }

    const marketServiceColumns = [
        {
            header: "Market Id",
            sticky: "left",
            size: 120,
            accessorKey: "index",
            filterable: true
        },
        {
            header: "Market Tag",
            sticky: "left",
            size: 120,
            accessorKey: "market_key",
            filterable: true
        },
        {
            header: "Description",
            sticky: "left",
            size: 460,
            accessorKey: "description",
            filterable: true,
        },
        {
            header: "Order No",
            accessorKey: "order_no",
            filterable: true,
        },
        {
            header: "EWB",
            accessorKey: "ewb",
            filterable: true,
        },
        {
            header: "Account",
            accessorKey: "account_id",
            filterable: true,
            size: 200,
            cell: ({getValue}) => getCellForService(getValue(), accountDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, accountDropdown),
        },
        {
            header: "Primary Outlet",
            accessorKey: "outlets_id",
            filterable: true,
            size: 225,
            cell: ({getValue}) => getCellForService(getValue(), outletsDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, outletsDropdown),
        },
        {
            header: "Secondary Outlet",
            accessorKey: "secondary_outlets",
            filterable: true,
            size: 250,
            cell: ({getValue}) => getArrayCellForService(getValue(), outletsDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterArrayValueForService(row, columnIds, filterValue, outletsDropdown),
        },
        {
            header: "Geo Lvl",
            accessorKey: "geography_level_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), geographyLevelDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, geographyLevelDropdown),
        },
        {
            header: "Geography Type",
            accessorKey: "geography_type_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), geographyTypeDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, geographyTypeDropdown),
        },
        {
            header: "Legacy Acct Type",
            accessorKey: "legacy_account_type_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), legacyAccountTypeDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, legacyAccountTypeDropdown),
        },
        {
            header: "Acct Type",
            accessorKey: "account_type_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), accountTypeDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, accountTypeDropdown),
        },
        {
            header: "Releasability",
            accessorKey: "releasability_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), releasabilityDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, releasabilityDropdown),
        },
        {
            header: "Weekly Releasability",
            accessorKey: "weekly_releasability_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), weeklyReleasabilityDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, weeklyReleasabilityDropdown),
        },
        {
            header: "Measure Avail",
            accessorKey: "measure_availability_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), measureAvailabilityDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, measureAvailabilityDropdown),
        },
        {
            header: "Control Brand",
            accessorKey: "control_brand",
            filterable: true,
        },
        {
            header: "Geo Comp",
            accessorKey: "geography_composition_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), geographyCompositionDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, geographyCompositionDropdown),
        },
        {
            header: "IRI HiCone Market",
            accessorKey: "iri_hicone_market",
            filterable: true,
        },
        {
            header: "Retailer Corp Totals",
            accessorKey: "retailer_corporate_totals",
            filterable: true,
            size: 190,
        },
        {
            header: "Activate Dist Tracker",
            accessorKey: "activate_for_distribution_tracker",
            filterable: true,
            size: 190,
        },
        {
            header: "Market Group Id",
            accessorKey: "market_group_id",
            filterable: true,
        },
        {
            header: "Manu Geo Family Id",
            accessorKey: "manufacturer_geo_family_id",
            filterable: true,
            size: 190,
        },
        {
            header: "Retailer Geo Family Id",
            accessorKey: "retailer_geo_family_id",
            filterable: true,
            size: 190,
        },
        {
            header: "Geo Family Desc",
            accessorKey: "geo_family_description",
            filterable: true,
        },
        {
            header: "Dataset",
            accessorKey: "dataset",
            filterable: true,
            cell: ({getValue}) => getArrayCellForService(getValue())
        },
        {
            header: "Geography Store Level Allowability",
            accessorKey: "geography_store_level_allowability_id",
            filterable: true,
            cell: ({getValue}) => getCellForService(getValue(), geoStoreLevelAllowabilityDropdown),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, geoStoreLevelAllowabilityDropdown),
        }
    ]


    async function onRowEditConfirmedWrapper(newOrUpdatedRow) {
        delete newOrUpdatedRow.retailer_geo_family_id;
        delete newOrUpdatedRow.manufacturer_geo_family_id;
        delete newOrUpdatedRow.geo_family_description;

        if (outletChannelSourceMap[newOrUpdatedRow.outlets_id] === 'CONVENTIONAL') {
            const fieldsToUpdate = {
                control_brand: {value: null, label: 'Control Brand'},
                geography_composition_id: {value: null, label: 'Geography Composition'},
                geography_store_level_allowability_id: {
                    value: geoStoreLevelAllowabilityDropdown.find(option => option.label === 'GEO ONLY')?.value,
                    label: 'Geography Store Level Allowability'
                },
                weekly_releasability_id: {value: null, label: 'Weekly Releasability'}
            };

            const changes = [];
            Object.keys(fieldsToUpdate).forEach(key => {
                const {value, label} = fieldsToUpdate[key];
                if (newOrUpdatedRow[key] !== value) {
                    newOrUpdatedRow[key] = value;
                    const displayValue = key === 'geography_store_level_allowability_id'
                        ? geoStoreLevelAllowabilityDropdown.find(option => option.value === value)?.label
                        : value;
                    changes.push(`${label}: ${displayValue}`);
                }
            });

            if (changes.length > 0) {
                window.alert(`The following fields have been changed because the selected outlet has a CONVENTIONAL channel source:\n${changes.join('\n')}`);
            }
        }

        Object.keys(newOrUpdatedRow).forEach(function (key) {
            if (newOrUpdatedRow[key] === undefined || newOrUpdatedRow[key] === "") {
                newOrUpdatedRow[key] = null;
            }
            if (isNaN(newOrUpdatedRow[key]) && !newOrUpdatedRow[key]) {
                newOrUpdatedRow[key] = null;
            }
        });

        await onRowEditConfirmed(newOrUpdatedRow);
    }

    function validatePreSave({form}) {
        const description = form.getValues('description');
        const descriptionErrors = [
            {regex: new RegExp(/^\s|\s$/), message: 'Description cannot have leading or trailing whitespace'},
            {regex: new RegExp(/\s\s/), message: 'Description cannot contain multiple consecutive whitespaces'},
            {regex: new RegExp(/ /), message: 'Description contains a non breaking space'}
        ].filter(e => e.regex.test(description))
            .map(e => e.message)

        if (descriptionErrors.length > 0) {
            form.setError('description', {
                type: 'custom',
                message: descriptionErrors
            });
        }

        const weeklyReleasabilityErrors = [];
        const channelSource = outletChannelSourceMap[form.getValues('outlets_id')];
        const weeklyReleasabilityId = form.getValues('weekly_releasability_id');
        const weeklyReleasabilityIsBlank = weeklyReleasabilityId === null
            || weeklyReleasabilityId === undefined
            || weeklyReleasabilityId === '';

        if (channelSource === 'SPINS' && weeklyReleasabilityIsBlank) {
            weeklyReleasabilityErrors.push('Weekly Releasability is required for Proprietary markets');
        }
        if (channelSource !== 'SPINS' && !weeklyReleasabilityIsBlank) {
            weeklyReleasabilityErrors.push('Weekly Releasability must be blank for Conventional markets');
        }

        if (weeklyReleasabilityErrors.length > 0) {
            form.setError('weekly_releasability_id', {
                type: 'custom',
                message: weeklyReleasabilityErrors
            });
        }

        return descriptionErrors.length === 0 && weeklyReleasabilityErrors.length === 0;
    }

    return (
        /*@formatter:off*/
        <div>
            <LoadingWrapper loading={loading || decodeTablesLoading}>
                <EditableReactTableV2
                    filterable
                    saveFilterStateToUrl={true}
                    saveSortStateToUrl={true}
                    savePageIndexToUrl={true}
                    data={data.data}
                    columns={marketServiceColumns}
                    initialState={{
                        pagination: {pageSize: 10},
                        sorting: [{id: "index", desc: true}]
                    }}
                    actionButtonsProps={{sticky: true}}
                    editTable={[
                        {id: 'index', Component: args => <IndexEditField {...args} />},
                        {id: 'market_key', Component: args => <EditFormNumberInputField {...args} isRequired/>},
                        {id: 'description', Component: args => <EditFormTextInputField {...args} isRequired/>},
                        {id: 'order_no', Component: args => <EditFormNumberInputField {...args}/>},
                        {id: 'account_id', Component: args => <EditFormDropdownInputField {...args} options={accountDropdown}/>},
                        {id: 'outlets_id', Component: args => <EditFormDropdownInputField {...args} options={outletsDropdown}/>},
                        {id: 'secondary_outlets', Component: args => <EditFormMultiDropdownInputField {...args} options={outletsDropdown}/>},
                        {id: 'geography_level_id', Component: args => <EditFormDropdownInputField {...args} options={geographyLevelDropdown}/>},
                        {id: 'account_type_id', Component: args => <EditFormDropdownInputField {...args} options={accountTypeDropdown}/>},
                        {id: 'releasability_id', Component: args => <EditFormDropdownInputField {...args} options={releasabilityDropdown}/>},
                        {id: 'measure_availability_id', Component: args => <EditFormDropdownInputField {...args} options={measureAvailabilityDropdown}/>},
                        {id: 'geography_type_id', Component: args => <EditFormDropdownInputField {...args} options={geographyTypeDropdown}/>},
                        {id: 'ewb', Component: args => <EwbEditField {...args} />},
                        {id: 'legacy_account_type_id', Component: args => <EditFormDropdownInputField {...args} options={legacyAccountTypeDropdown}/>},
                        {id: 'geography_composition_id', Component: args => <EditFormDropdownInputField {...args} options={geographyCompositionDropdown}/>},
                        {id: 'manufacturer_geo_family_id', Component: args => <EditFormNumberInputField {...args} disabled />},
                        {id: 'retailer_geo_family_id', Component: args => <EditFormNumberInputField {...args} disabled />},
                        {id: 'geo_family_description', Component: args => <EditFormTextInputField {...args} disabled />},
                        {id: 'retailer_corporate_totals', defaultValue: false, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                        {id: 'market_group_id', Component: args => <EditFormTextInputField {...args} />},
                        {id: 'control_brand', defaultValue: false, Component: args => <EditFormBooleanDropdownInputField {...args} isClearable />},
                        {id: 'iri_hicone_market', defaultValue: false, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                        {id: 'activate_for_distribution_tracker', defaultValue: false, Component: args => <EditFormBooleanDropdownInputField {...args} />},
                        {id: 'geography_store_level_allowability_id', defaultValue: 0, Component: args => <EditFormDropdownInputField {...args} options={geoStoreLevelAllowabilityDropdown}/>},
                        {id: 'weekly_releasability_id', Component: args => <EditFormDropdownInputField {...args} options={weeklyReleasabilityDropdown} isClearable/>},
                    ]}
                    editModalAdditionalClass={"market-service-edit-modal"}
                    onRowEditConfirmed={onRowEditConfirmedWrapper}
                    onRowDeleteConfirmed={onRowDeleteConfirmed}
                    validatePreSave={validatePreSave}
                >
                    {
                        ({table}) => {
                            const rows = table.getCoreRowModel().rows.map(row => row.original);
                            const duplicateIndices = findDuplicates(rows, ['index']).index ?? {};
                            const duplicateMarketKeys = findDuplicates(rows, ['market_key']).market_key ?? {};

                            return (
                                <div>
                                    <div className={'market-service-content-controls'}>
                                        <IconDropdown iconName={'fa-cog'} title={"Services"}>
                                            <MarketServiceNavLinks period={period}/>
                                        </IconDropdown>
                                        <div>
                                            {(duplicateIndices.length > 0) &&
                                                <div className={'duplicate-warning-message'}>
                                                    {`Duplicated Market Ids: ${duplicateIndices}`}
                                                </div>
                                            }
                                            {(duplicateMarketKeys.length > 0) &&
                                                <div className={'duplicate-warning-message'}>
                                                    {`Duplicated Market Tags: ${duplicateMarketKeys}`}
                                                </div>
                                            }
                                        </div>
                                    </div>

                                </div>
                            )
                        }
                    }
                </EditableReactTableV2>
            </LoadingWrapper>
        </div>
        /*@formatter:on*/
    );
};

MarketServiceContent.propTypes = {
    period: PropTypes.string.isRequired,
    outletsDropdown: PropTypes.array,
    accountDropdown: PropTypes.array,
    legacyAccountTypeDropdown: PropTypes.array,
    geographyLevelDropdown: PropTypes.array,
    accountTypeDropdown: PropTypes.array,
    geographyTypeDropdown: PropTypes.array,
    geographyCompositionDropdown: PropTypes.array,
    releasabilityDropdown: PropTypes.array,
    weeklyReleasabilityDropdown: PropTypes.array,
    measureAvailabilityDropdown: PropTypes.array,
    geoStoreLevelAllowabilityDropdown: PropTypes.array,
    decodeTablesLoading: PropTypes.bool,
    outletChannelSourceMap: PropTypes.object
};

export default MarketServiceContent;
